// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Customer dashboard route module.
 * Provides a comprehensive overview of customer metrics and reports.
 *
 * Features:
 * - Functional status distribution
 * - Ownership breakdown
 * - Responsive grid layout
 * - Sort preferences
 *
 * URL Parameters:
 * - sort: Sort property for reports (count, name)
 * - direction: Sort direction (ascending/descending)
 *
 * Layout:
 * - Desktop: 2 columns with cards filling available space
 * - Mobile: Stacked cards with full width
 */
import { CustomerFunctionalStatusReportCard } from '@/customer/components/customer-functional-status-report-card';
import { CustomerOwnershipReportCard } from '@/customer/components/customer-ownership-report-card';
import { Box } from '@mui/material';
import { createFileRoute } from '@tanstack/react-router';

/**
 * Customer dashboard route configuration.
 * Sets up the component for the customer dashboard view.
 */
export const Route = createFileRoute('/_app/dashboard/customer')({
  component: CustomerDashboardRoute,
});

/**
 * Main customer dashboard route component.
 * Displays a grid of customer-related metrics and reports.
 *
 * Features:
 * - Responsive grid layout
 * - Consistent sort handling
 * - Mobile-friendly stacking
 *
 * Grid Layout:
 * - Desktop: 2 columns with cards filling available space
 * - Mobile: Single column with stacked cards
 */
function CustomerDashboardRoute() {
  const searchParameters = Route.useSearch();

  /**
   * Current sort configuration from URL parameters.
   * Defaults to descending count sort if not specified.
   */
  const sort = {
    property: searchParameters.sort ?? 'count',
    direction: searchParameters.direction ?? 'desc',
  };

  // Maximum height for chart containers on this dashboard
  const chartMaxHeight = '50rem';

  return (
    <Box
      flex={1}
      display="grid"
      gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }}
      gridTemplateRows="1fr 1fr"
      gap={2}
    >
      <CustomerFunctionalStatusReportCard
        sort={sort}
        maximumHeight={chartMaxHeight}
      />
      <CustomerOwnershipReportCard sort={sort} maximumHeight={chartMaxHeight} />
    </Box>
  );
}
